import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { LocalDocumentItem, isPendingDocument } from './JobDocuments';
import DraggableDocumentItem from './DraggableDocumentItem';
import AddDocumentInput from './AddDocumentInput';

interface EditableDocumentListProps {
  documents: LocalDocumentItem[];
  onAdd: (file: File) => Promise<void>;
  onDelete: (id: number | string) => void;
  onLocalReorder: (reorderedDocuments: LocalDocumentItem[]) => void;
  onDescriptionChange: (id: number | string, newDescription: string) => void;
  onRefNumberChange: (id: number | string, newRefNumber: string) => void;
  onDocumentTypeChange: (id: number | string, newType: string, paperColor?: string) => void;
  isUploading: boolean;
  currentTotalSizeBytes: number;
  editingField: string | null;
  onEditingFieldChange: (fieldKey: string | null) => void;
}

const EditableDocumentList = ({
  documents,
  onAdd,
  onDelete,
  onLocalReorder,
  onDescriptionChange,
  onRefNumberChange,
  onDocumentTypeChange,
  isUploading,
  currentTotalSizeBytes,
  editingField,
  onEditingFieldChange,
}: EditableDocumentListProps) => {
  const isDragDisabled = documents.length <= 1;

  const getItemId = (doc: LocalDocumentItem): string | number => {
    return isPendingDocument(doc) ? doc.tempId : doc.id;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = documents.findIndex((doc) => getItemId(doc) === active.id);
    const newIndex = documents.findIndex((doc) => getItemId(doc) === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(documents, oldIndex, newIndex);
    onLocalReorder(reordered);
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.5rem 0.75rem',
    borderBottom: '2px solid #e0e0e0',
    backgroundColor: '#fafafa',
    fontWeight: 600,
    fontSize: '0.8125rem',
    color: '#424242',
  };

  const headerCellStyle: React.CSSProperties = {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  return (
    <div>
      <div style={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}>
        <div style={headerStyle}>
          {!isDragDisabled && <span style={{ width: '24px' }} />}
          <span style={{ ...headerCellStyle, flex: 1.5 }}>Files</span>
          <span style={{ ...headerCellStyle, flex: 1.5 }}>Description</span>
          <span style={{ ...headerCellStyle, flex: 0.7, whiteSpace: 'normal', textAlign: 'center' }}>ECF Docket Ref. No.</span>
          <span style={{ ...headerCellStyle, flex: 1.2 }}>Document Type</span>
          <span style={{ ...headerCellStyle, flex: 1 }}>Paper Color</span>
          <span style={{ ...headerCellStyle, flex: 0.5, textAlign: 'center' }}>Pages</span>
          <span style={{ width: '32px' }} />
        </div>

        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={documents.map((doc) => getItemId(doc))}
            strategy={verticalListSortingStrategy}
          >
            {documents.map((document) => (
              <DraggableDocumentItem
                key={getItemId(document)}
                document={document}
                documents={documents}
                onDelete={onDelete}
                onDescriptionChange={onDescriptionChange}
                onRefNumberChange={onRefNumberChange}
                onDocumentTypeChange={onDocumentTypeChange}
                isDragDisabled={isDragDisabled}
                editingField={editingField}
                onEditingFieldChange={onEditingFieldChange}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      <AddDocumentInput onAdd={onAdd} isLoading={isUploading} currentDocumentCount={documents.length} currentTotalSizeBytes={currentTotalSizeBytes} />
    </div>
  );
};

export default EditableDocumentList;
